import { ReactNode } from "react";
import { motion, useReducedMotion, Variants } from "framer-motion";
import { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { GuardBackground } from "./GuardBackground";
import { GuardTone, GUARD_TONES } from "./guard-tones";

interface GuardStatePanelProps {
  icon: LucideIcon;
  title: string;
  description: ReactNode;
  /** Cor do acento/halo. Padrão: 'destructive'. */
  tone?: GuardTone;
  /** Botões de ação (renderizados numa linha responsiva). */
  actions?: ReactNode;
  /** Conteúdo rico entre a descrição e as ações (ex.: cards de outras visões). */
  children?: ReactNode;
  /** Conteúdo extra abaixo da descrição (ex.: debug em dev). */
  footer?: ReactNode;
  className?: string;
}

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

/**
 * Tela de estado bloqueado/erro usada pelos HOCs de guarda (permissão, job,
 * planilha). Ocupa a página inteira: fundo com halo do tom, ícone que entra em
 * mola, e conteúdo em cascata. Sem movimento quando o sistema pede menos.
 */
export function GuardStatePanel({
  icon: Icon,
  title,
  description,
  tone = "destructive",
  actions,
  children,
  footer,
  className,
}: Readonly<GuardStatePanelProps>) {
  const t = GUARD_TONES[tone];
  const semMovimento = useReducedMotion();

  return (
    <div
      className={cn(
        "relative flex min-h-[70vh] w-full items-center justify-center overflow-hidden px-4 py-12",
        className,
      )}
    >
      <GuardBackground tone={tone} />

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 flex w-full max-w-2xl flex-col items-center text-center"
      >
        <motion.div
          className="relative mb-7"
          initial={semMovimento ? false : { scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 18 }}
        >
          <motion.span
            aria-hidden
            className={cn("absolute inset-0 rounded-3xl blur-xl", t.halo)}
            animate={
              semMovimento ? undefined : { opacity: [0.5, 1, 0.5], scale: [0.95, 1.15, 0.95] }
            }
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
          />
          <div
            className={cn(
              "relative flex h-20 w-20 items-center justify-center rounded-3xl ring-1 ring-inset backdrop-blur-sm",
              t.badge,
            )}
          >
            <Icon className={cn("h-9 w-9", t.icon)} strokeWidth={1.75} />
          </div>
        </motion.div>

        <motion.h1
          variants={item}
          className="text-balance text-3xl font-semibold tracking-tight text-foreground sm:text-4xl"
        >
          {title}
        </motion.h1>

        <motion.p
          variants={item}
          className="mt-4 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg"
        >
          {description}
        </motion.p>

        {footer && (
          <motion.div variants={item} className="mt-4 flex w-full items-center justify-center">
            {footer}
          </motion.div>
        )}

        {children && (
          <motion.div variants={item} className="mt-9 w-full">
            {children}
          </motion.div>
        )}

        {actions && (
          <motion.div
            variants={item}
            className="mt-9 flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-center"
          >
            {actions}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
